
import { useState, useEffect, useCallback } from 'react';
import { Subscription } from '@/services/types/subscriptionTypes';
import { 
  checkAdminAccess,
  checkSubscriptionsCount,
  fetchAllSubscriptions
} from '../api/subscriptionApi';
import { validateSubscriptionStatus } from '@/pages/admin/rentals/services/subscriptionCommon';
import { toast } from '@/components/ui/sonner';

export const useSubscriptionsStatus = (
  setSubscriptionsCount?: (count: number | null) => void
) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Проверка прав администратора
      const { hasAccess, role, error: accessError } = await checkAdminAccess();
      
      if (accessError) {
        console.error('Ошибка проверки доступа:', accessError);
        setError('Не удалось проверить права доступа');
        return;
      }
      
      if (!hasAccess) {
        console.error(`Недостаточно прав для просмотра. Роль: ${role}`);
        setError(`Доступ запрещен. Ваша роль: ${role}`);
        return;
      }
      
      // Проверка количества записей
      const { count, error: countError } = await checkSubscriptionsCount();
      
      if (countError) {
        console.error('Ошибка при проверке количества подписок:', countError);
      } else {
        console.log(`Всего подписок в базе: ${count}`);
        
        if (setSubscriptionsCount) {
          setSubscriptionsCount(count);
        }
        
        if (count === 0) {
          setSubscriptions([]);
          setLoading(false);
          return;
        }
      }
      
      // Загрузка подписок
      const { data, error: fetchError } = await fetchAllSubscriptions();
      
      if (fetchError) {
        console.error('Ошибка при загрузке подписок:', fetchError);
        setError('Не удалось загрузить подписки');
        toast.error('Ошибка загрузки данных о подписках', {
          duration: 5000 // 5 секунд для уведомления
        });
      } else {
        console.log(`Загружено ${data.length} подписок`);
        
        // Преобразуем данные с правильной типизацией
        const formattedData: Subscription[] = data.map(sub => ({
          ...sub,
          interval: sub.interval as "day" | "week" | "month" | "year",
          status: validateSubscriptionStatus(sub.status)
        }));
        
        setSubscriptions(formattedData);
      }
      
    } catch (err: any) {
      console.error('Непредвиденная ошибка:', err);
      setError(`Произошла ошибка: ${err.message}`);
      toast.error('Не удалось загрузить данные', {
        duration: 5000 // 5 секунд для уведомления
      });
    } finally {
      setLoading(false);
    }
  }, [setSubscriptionsCount]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  return { subscriptions, loading, error, fetchData };
};
